import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Table, Space, Button, Typography, Spin, Empty, message,
  Image as AntImage, Card, Descriptions, Tooltip, Row, Col, Tag, Input,
} from "antd";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeftOutlined, CopyOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getRequest } from "../../util/service";
import { generateImagesFromSmiles } from "../../util/ChemDrawUtils";
import { FilterCriteriaDisplay } from "../shared/FilterCriteriaDisplay";
import BreadcrumbTrail from "../shared/BreadcrumbTrail";

const { Title, Text } = Typography;

const getStatusProps = (status) => {
  const value = String(status || "");
  const lower = value.toLowerCase();
  if (lower.includes("complete")) return { color: "green", text: value || "Completed" };
  if (lower.includes("fail") || lower.includes("error")) return { color: "red", text: value || "Failed" };
  if (lower.includes("progress")) return { color: "blue", text: value || "In Progress" };
  if (lower.includes("terminate") || lower.includes("cancel")) return { color: "orange", text: value || "Terminated" };
  return { color: "default", text: value || "N/A" };
};

export default function AdmeMolecularDataPage() {
  const navigate = useNavigate();
  const { projectId, batchId, recordId } = useParams();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [jobDetails, setJobDetails] = useState(null);
  const [molecularData, setMolecularData] = useState([]);
  const [admeProperties, setAdmeProperties] = useState([]);
  const [imgCache, setImgCache] = useState({});
  const inFlightRef = useRef(new Set());
  const [searchValue, setSearchValue] = useState(location.state?.prefillMoleculeId || "");

  const fetchAdmeData = useCallback(async () => {
    if (!recordId) return;
    setLoading(true);
    setError("");
    try {
      const resp = await getRequest(`/riva/adme/properties/${recordId}`);
      const data = resp?.data?.[0];

      if (!data) throw new Error("No data found for this ADME job.");
      
      setJobDetails(data);
      setMolecularData(data.adme_molecule_data || []);

      if (data.adme_molecule_data?.[0]?.adme_data) {
        setAdmeProperties(Object.keys(data.adme_molecule_data[0].adme_data));
      }
    } catch (err) {
      const msg = err?.message || "An error occurred while fetching ADME data.";
      setError(msg);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  useEffect(() => {
    fetchAdmeData();
  }, [fetchAdmeData]);

  useEffect(() => {
    const smilesToFetch = molecularData.map(m => m.smile_string).filter(s => s && !imgCache[s] && !inFlightRef.current.has(s));
    if (smilesToFetch.length === 0) return;

    smilesToFetch.forEach(s => inFlightRef.current.add(s));
    (async () => {
        try {
            const urls = await generateImagesFromSmiles(smilesToFetch);
            const newImages = {};
            smilesToFetch.forEach((s, i) => { newImages[s] = urls[i]; });
            setImgCache(prev => ({...prev, ...newImages}));
        } catch (e) { /* Silently fail on image generation error */ } 
        finally {
            smilesToFetch.forEach(s => inFlightRef.current.delete(s));
        }
    })();
  }, [molecularData, imgCache]);

  const handleCloneJob = () => {
    if (!jobDetails) return;
    navigate(`/project/${projectId}/batch/${batchId}/submit`, {
      state: {
        selectedMolecules: jobDetails.molecule_data.map(m => ({ molecule_id: m.smileId, smile: m.smileString })),
        filterCriteria: jobDetails.filter_criteria,
        jobToClone: {
          selection_name: jobDetails.ms_name,
          selection_desc: jobDetails.ms_description,
          method: "adme",
          properties: jobDetails.adme_input?.properties || [], 
          selection_id: jobDetails.ms_record_id,
        },
      },
    });
  };

  const columns = useMemo(() => {
  const baseColumns = [
    { title: "Molecule ID", dataIndex: "smile_id", key: "smile_id", width: 180, fixed: "left" },
    {
      title: "2D Structure",
      dataIndex: "smile_string",
      key: "structure",
      width: 200,
      align: "center",
      render: (smile) => {
        const url = imgCache[smile];
        return (
          <div style={{ width: 200, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div style={{ height: 140, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {url ? <AntImage src={url} style={{ width: 120, height: 120, objectFit: "contain" }} /> : <Spin size="small" />}
            </div>
            <div style={{ width: "80%", borderTop: "1px solid #e0e0e0" }} />
            <Tooltip title="Copy SMILES">
              <Button size="small" icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(smile || ""); message.success("SMILES Copied!"); }} />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const dynamicColumns = admeProperties.map((propName) => {
    const lastDot = propName.lastIndexOf(".");
    const parent = lastDot !== -1 ? propName.slice(0, lastDot) : propName;
    const child = lastDot !== -1 ? propName.slice(lastDot + 1) : "";
    const title = child ? `${parent}(${child})` : parent;
    return {
      title,
      dataIndex: ["adme_data", propName],
      key: propName,
      width: 220,
      align: "center",
      render: (admeObject) => {
        const value = admeObject?.value;
        if (value === null || value === undefined) return "-";
        if (typeof value === "boolean") {
          return value ? <Tag color="green">True</Tag> : <Tag color="red">False</Tag>;
        }
        if (typeof value === "number") {
          return value.toFixed(3);
        }
        return String(value);
      },
    };
  });

  return [...baseColumns, ...dynamicColumns];
}, [admeProperties, imgCache]);

  const filteredData = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) return molecularData;
    return molecularData.filter((item) =>
      String(item.smile_id || "").toLowerCase().includes(term)
    );
  }, [searchValue, molecularData]);

if (loading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column", // Stack the spinner and text vertically
        alignItems: "center",
        justifyContent: "center",
        height: "calc(100vh - 200px)" // Adjust height to be below the header
      }}>
        <Spin size="large" />
        <Text style={{ marginTop: "16px" }}>Loading ADME Data...</Text>
      </div>
    );
  }
  
  if (error) {
    return <Empty description={error} style={{ marginTop: "48px" }} />;
  }

  return (
    <div style={{ padding: 24, background: "#fff", minHeight: "100vh" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, marginTop: 70 }}>
        <Col>
          <Space align="center">
            <Tooltip title="Back to Results">
              <Button onClick={() => navigate(-1)} icon={<ArrowLeftOutlined />} />
            </Tooltip>
            <Title level={3} style={{ margin: 0 }}>ADME Result</Title>
          </Space>
        </Col>
      </Row>
      <BreadcrumbTrail />
 
      {/* ✅ FIX: Standardized Info Cards */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card bordered title={<Text strong>Selection Details</Text>} bodyStyle={{ padding: "12px 16px" }}>
            <Descriptions column={1} layout="horizontal" bordered size="small">
              <Descriptions.Item label="Name">{jobDetails?.ms_name || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Description">{jobDetails?.ms_description || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Filter Criteria" contentStyle={{ display: "block", overflow: "hidden" }}>
                <FilterCriteriaDisplay rules={jobDetails?.filter_criteria} />
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col span={12}>
          <Card bordered title={<Text strong>User Inputs</Text>} bodyStyle={{ padding: "12px 16px" }}>
            <Descriptions column={1} layout="horizontal" bordered size="small">
              <Descriptions.Item label="Submitted By">{jobDetails?.submitted_id || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Submitted Time">{jobDetails?.submitted_ts ? dayjs(jobDetails.submitted_ts).format("YYYY-MM-DD HH:mm:ss") : "N/A"}</Descriptions.Item>
              {(jobDetails?.status || jobDetails?.status_message) && (
                <Descriptions.Item label="Status / Message">
                  <Space>
                    <Tag color={getStatusProps(jobDetails?.status).color}>{getStatusProps(jobDetails?.status).text}</Tag>
                    {jobDetails?.status_message && <Text type="secondary">{jobDetails.status_message}</Text>}
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>
      </Row>
      <Space style={{ width: "100%", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap", rowGap: 12 }}>
        <Input.Search
          placeholder="Search by Molecule ID"
          allowClear
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onSearch={(val) => setSearchValue(val)}
          style={{ width: 280 }}
        />
        <Button icon={<CopyOutlined />} onClick={handleCloneJob}>
          Clone Job
        </Button>
      </Space>

      <Table
        dataSource={filteredData}
        columns={columns}
        rowKey="md_record_id"
        pagination={{ pageSize: 10 }}
        size="small"
        bordered
        scroll={{ x: "max-content" }}
        locale={{ emptyText: <Empty description="No molecular data for this job." /> }}
      />
    </div>
  );
}







